import { BetaBadge } from "@doculegis/shared";
import { SUITE_TITLE } from "@/lib/apps";
import { cn } from "@/lib/utils";

interface BrandLogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  compact?: boolean;
  title?: string;
  showBeta?: boolean;
}

/** Default width (px) for the logo + title column. */
export const BRAND_LOGO_BADGE_WIDTH_PX = 220;

/** Title line size inside the bordered label under the logo. */
const subtitleSizeClasses = {
  sm: "text-[11px]",
  md: "text-xs",
  lg: "text-sm",
  xl: "text-base",
} as const;

export function BrandLogo({
  className,
  size = "md",
  compact = false,
  title,
  showBeta = true,
}: BrandLogoProps) {
  const displayTitle = title ?? SUITE_TITLE;

  const logoWidthPx = showBeta
    ? BRAND_LOGO_BADGE_WIDTH_PX + 48
    : BRAND_LOGO_BADGE_WIDTH_PX;

  return (
    <div
      aria-label={`Doculegis — ${displayTitle}`}
      style={{ width: `min(100%, ${logoWidthPx}px)` }}
      className={cn("inline-flex w-full max-w-full flex-col items-stretch gap-2", className)}
    >
      <div
        className={cn(
          "flex w-full min-w-0 shrink-0 items-start",
          showBeta && "gap-2"
        )}
      >
        <a
          href={import.meta.env.VITE_APP_URL || "/"}
          className={cn(
            "block min-w-0",
            showBeta ? "flex-1" : "w-full"
          )}
          aria-label="Doculegis home"
        >
          <img
            src="/doculegis_logo_white.svg"
            alt="Doculegis"
            className={cn(
              "block h-auto w-full min-w-0 object-contain object-top",
              compact ? "object-center" : "object-left"
            )}
          />
        </a>
        {showBeta ? <BetaBadge className="mt-0.5 shrink-0" /> : null}
      </div>

      <div
        role="note"
        className="w-full min-w-0 rounded-[10px] border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-3 py-2 dark:border-violet-400/25 dark:bg-violet-500/[0.08] dark:shadow-sm dark:backdrop-blur-sm"
      >
        <p
          lang="en"
          className={cn(
            "m-0 w-full text-center font-medium leading-snug text-[var(--lm-muted)]",
            "hyphens-auto break-words dark:text-slate-200",
            subtitleSizeClasses[size]
          )}
        >
          {displayTitle}
        </p>
      </div>
    </div>
  );
}
